declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

function gtag(...args: unknown[]) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag(...args)
  }
}

// --- Custom events ---

export function trackArticleView(slug: string, title: string) {
  gtag("event", "article_view", {
    article_slug: slug,
    article_title: title,
  })
}

export type CtaLocation =
  | "article_bottom"
  | "article_inline"
  | "header"
  | "hero"
  | "footer"
  | "nav"
  | "pricing_free"
  | "pricing_pro"
  | "final_cta"
  | "quiz_plan"
  | "press_button"
  | "hero_top"
  | "article_mid"
  | "prompt_widget"

export function trackCtaClick(
  location: CtaLocation,
  destination: "appstore" | "mac_appstore" = "appstore"
) {
  gtag("event", "cta_click", {
    cta_location: location,
    cta_destination: destination,
  })
}

/**
 * Site-wide click delegation: any anchor pointing at apps.apple.com fires
 * `cta_click` with a location derived from the anchor's `data-cta`
 * attribute (when present) or its DOM ancestry. Destination is inferred
 * from the URL (`?platform=mac` → `mac_appstore`).
 *
 * Wired up once in gatsby-browser.ts so it covers landing CTAs, the
 * Layout header, article-bottom badges, the quiz plan, the press page,
 * and inline MDX `[Mindspace](apps.apple.com/...)` links uniformly.
 */
export function installAppStoreClickTracking() {
  if (typeof document === "undefined") return

  const onClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null
    const anchor = target?.closest<HTMLAnchorElement>(
      'a[href*="apps.apple.com"]'
    )
    if (!anchor) return

    const dataCta = anchor.dataset.cta as CtaLocation | undefined
    const location: CtaLocation = dataCta ?? "article_inline"
    const destination =
      anchor.href.includes("platform=mac") ? "mac_appstore" : "appstore"

    trackCtaClick(location, destination)
  }

  document.addEventListener("click", onClick)
}

export function trackQuizStart() {
  gtag("event", "quiz_start")
}

export function trackQuizAnswer(
  questionId: string,
  questionIndex: number,
  answer: string | string[]
) {
  gtag("event", "quiz_answer", {
    question_id: questionId,
    question_index: questionIndex,
    answer: Array.isArray(answer) ? answer.join(",") : answer,
  })
}

export function trackQuizComplete() {
  gtag("event", "quiz_complete")
}

/**
 * Per-prompt actions on prompt-list articles (currently just `copy`). The
 * prompt text is truncated so the param stays under GA4's 100-char limit.
 */
export function trackPromptAction(action: "copy" | "random", prompt: string) {
  gtag("event", "prompt_action", {
    prompt_action: action,
    prompt_text: prompt.slice(0, 90),
  })
}

export function trackScrollDepth(slug: string, depth: number) {
  gtag("event", "scroll_depth", {
    article_slug: slug,
    depth_percent: depth,
  })
}

// --- Scroll depth observer ---

const firedDepths = new Set<string>()

export function observeScrollDepth(slug: string) {
  if (typeof window === "undefined") return

  firedDepths.clear()

  const thresholds = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

  const onScroll = () => {
    const scrollTop = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return

    const percent = Math.round((scrollTop / docHeight) * 100)

    for (const t of thresholds) {
      const key = `${slug}-${t}`
      if (percent >= t && !firedDepths.has(key)) {
        firedDepths.add(key)
        trackScrollDepth(slug, t)
      }
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true })

  return () => {
    window.removeEventListener("scroll", onScroll)
  }
}
